import {
  Avatar,
  Box,
  Divider,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React from 'react'
import { Fade, Icons, SerialNumber, SkeletonText } from 'utoniq-ui'

import {
  ItemInformationQuery,
  Maybe,
  SerialItemOwnersFragment,
  SkuSettings,
  UserInfoFragment,
  UserItemSerial,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  ItemDescription,
  ItemDescriptionSkeleton,
} from '@/components/organisms'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  fetching: boolean
  userId?: string
  itemId: string
  artistId: string
  hasNextPage: boolean
  item: ItemInformationQuery['item']
  user: Maybe<UserInfoFragment>
}

export type Action = {
  onFetchMore: () => Promise<void>
}

export const ConsoleItemInformationTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('itemInformation')
  const { routerNames, routerPages, generatePath } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: generatePath(routerPages.ConsoleItemDetail, {
            itemId: state.itemId,
          }),
        }}
      >
        <Box sx={{ pt: 3, px: 5 }}>
          {state.loading || !state.item ? (
            <ItemDescriptionSkeleton />
          ) : (
            <Fade in>
              <ItemDescription
                hiddenSku
                showRemaining
                remainingNumber={state.item?.formattedRemainingNumber}
                item={state.item}
                user={null}
                isThoughTicket={!!state.item.metadata?.ticket?.isThroughTicket}
              />
            </Fade>
          )}
        </Box>
        {state.item?.metadata?.ticket?.note && (
          <Stack spacing={1} sx={{ my: 4, px: 5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Icons.Info fontSize="small" sx={{ color: 'grey.600' }} />
              <Typography variant="h6" color="text.secondary">
                {t('ticketNote')}
              </Typography>
            </Stack>
            <Typography variant="body2">
              {state.item?.metadata?.ticket?.note}
            </Typography>
          </Stack>
        )}

        {state.item?.skuSettings && (
          <SkuListCell
            loading={state.loading}
            skuSettings={state.item?.skuSettings}
            defaultThumbnailUri={state.item?.thumbnail.front?.mediumUri ?? ''}
          />
        )}
        <SerialOwnersCell
          itemId={state.itemId}
          itemSerials={state.item?.serials ?? null}
          onFetchMore={onFetchMore}
          showSku={!!state.item?.skuSettings?.units.length}
          hasNextPage={state.hasNextPage}
          loading={state.loading || state.fetching}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}
type SkuListCellProps = {
  loading: boolean
  skuSettings?: Maybe<SkuSettings>
  defaultThumbnailUri: string
}
const SkuListCell: Cell<SkuListCellProps> = ({
  loading,
  skuSettings,
  defaultThumbnailUri,
}) => {
  const { t } = useTranslation('consoleItemInformation')
  const unitLength = skuSettings?.units.length ?? 0
  return (
    <CellFrame sx={{ pt: 7 }}>
      <Typography
        variant="subtitle1"
        color="text.secondary"
        sx={{ py: 2, px: 5 }}
      >
        {t('skuLabel', { length: unitLength > 0 ? `（${unitLength}）` : '' })}
      </Typography>
      <Box sx={{ pb: 2, px: 5 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: 'none', py: 2, px: 0 }}>
                  <Typography variant="overline" sx={{ display: 'block' }}>
                    {t('skuName', 'SKU値')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', width: 100, py: 2, px: 0 }}>
                  <Typography variant="overline" sx={{ display: 'block' }}>
                    {t('inventory', '残り数量/全数量')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ border: 'none', width: 80, py: 2, px: 0 }}>
                  <Typography variant="overline" sx={{ display: 'block' }}>
                    {t('thumbnailImage', 'サムネイル画像')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !skuSettings?.units && (
                <TableRow>
                  <TableCell padding="none" sx={{ py: 2, px: 0 }}>
                    <SkeletonText variant="small" width={80} />
                  </TableCell>
                  <TableCell padding="none" sx={{ py: 2, px: 0 }}>
                    <Skeleton variant="small" width={40} />
                  </TableCell>
                  <TableCell padding="none" sx={{ py: 2, px: 0 }}>
                    <Skeleton
                      variant="rectangular"
                      sx={{ width: 48, height: 76 }}
                    />
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                skuSettings?.units?.map(unit => (
                  <TableRow key={unit.id}>
                    <TableCell padding="none" sx={{ py: 2, px: 0 }}>
                      <Typography variant="subtitle2">
                        {unit.unitName}
                      </Typography>
                    </TableCell>
                    <TableCell padding="none" sx={{ py: 2, px: 0 }}>
                      <Typography variant="subtitle2">
                        {unit.remainingNumber}/{unit.stock}
                      </Typography>
                    </TableCell>
                    <TableCell padding="none" sx={{ py: 2, px: 0 }}>
                      <Avatar
                        variant="square"
                        src={
                          unit.thumbnail?.smallUri ??
                          unit.thumbnail?.mediumUri ??
                          unit.thumbnail?.uri ??
                          defaultThumbnailUri ??
                          ''
                        }
                        sx={{ width: 48, height: 76 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </CellFrame>
  )
}

type SerialOwnersCellProps = {
  itemId: string
  showSku?: boolean
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  itemSerials: Maybe<SerialItemOwnersFragment>
  onFetchMore: Action['onFetchMore']
}
const SerialOwnersCell: Cell<SerialOwnersCellProps> = ({
  itemId,
  showSku,
  loading,
  hasNextPage,
  itemSerials,
  onFetchMore,
}) => {
  const { t } = useTranslation('itemInformation')
  const { routerPages, generatePath } = useRouter()

  const serials = itemSerials?.edges?.map(edge => edge) ?? []

  if (!serials?.length) return null

  return (
    <CellFrame sx={{ pt: 7 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ py: 2, px: 5 }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          {t('ownerList')}
        </Typography>
        <Link
          variant="subtitle1"
          href={generatePath(routerPages.ConsoleItemInformationLimitUser, {
            itemId,
          })}
        >
          {t('availablePurchasers', '購入可能者一覧を見る')}
        </Link>
      </Stack>
      <Box sx={{ pb: 2 }}>
        <TableContainer>
          <Table aria-label="serial-number-table" sx={{ width: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: 'none', minWidth: 84, py: 2, px: 5 }}>
                  <Typography
                    variant="overline"
                    color="text.hint"
                    sx={{ display: 'block' }}
                  >
                    {t('serialNumber')}
                  </Typography>
                </TableCell>
                {showSku && (
                  <TableCell
                    align="left"
                    sx={{ border: 'none', minWidth: 100, py: 2, px: 3 }}
                  >
                    <Typography variant="overline" color="text.hint">
                      {t('sku')}
                    </Typography>
                  </TableCell>
                )}
                <TableCell
                  align="left"
                  sx={{ border: 'none', minWidth: 100, py: 2, px: 3 }}
                >
                  <Typography variant="overline" color="text.hint">
                    {t('acquisitionDate')}
                  </Typography>
                </TableCell>
                <TableCell
                  width="100%"
                  align="left"
                  sx={{ border: 'none', py: 2, px: 3 }}
                >
                  <Typography variant="overline" color="text.hint">
                    {t('owner')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableScrollFetcher
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMore}
              component={TableRow}
              colSpan={3}
              nodes={serials?.map(serial => {
                const serialUser = serial.node.user
                const isNft = isUserItemSerial(serial)
                  ? !!serial.isNft
                  : !!serial.node.isNft

                return (
                  <>
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        pl: 5,
                        pr: 0,
                        border: 'none',
                        bgcolor: isUserItemSerial(serial) ? 'grey.50' : 'unset',
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ pr: 2 }}
                      >
                        <SerialNumber
                          label={serial.node.formattedSerialNumber}
                        />
                        {isNft && <Icons.Nft fontSize="small" filledColor />}
                      </Stack>
                    </TableCell>
                    {showSku && (
                      <TableCell
                        padding="none"
                        sx={{
                          py: 2,
                          px: 0,
                          border: 'none',
                          bgcolor: isUserItemSerial(serial)
                            ? 'grey.50'
                            : 'unset',
                        }}
                      >
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ height: 20 }}
                          />
                          <Typography variant="subtitle2">
                            {serial.node.sku?.unitName}
                          </Typography>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        px: 0,
                        border: 'none',
                        bgcolor: isUserItemSerial(serial) ? 'grey.50' : 'unset',
                      }}
                    >
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ height: 20 }}
                        />
                        {serialUser && (
                          <Typography variant="overline">
                            {serial.node?.formattedObtainedDate}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell
                      padding="none"
                      sx={{
                        py: 2,
                        pl: 0,
                        pr: 5,
                        border: 'none',
                        bgcolor: isUserItemSerial(serial) ? 'grey.50' : 'unset',
                      }}
                    >
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Stack>
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ height: 20 }}
                          />
                        </Stack>
                        {serialUser && (
                          <Stack
                            component={Link}
                            underline="none"
                            href={generatePath(routerPages.ConsoleUserDetail, {
                              userId: serialUser.id,
                            })}
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Avatar
                              src={serialUser.avatar?.smallUri ?? ''}
                              sx={{ width: 28, height: 28 }}
                            />
                            <Typography variant="overline">
                              {serial.node.user?.displayName}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </TableCell>
                  </>
                )
              })}
              loadingComponent={
                <TableRow>
                  <TableCell padding="none" colSpan={3} sx={{ border: 'none' }}>
                    <Stack flex={1} spacing={3} sx={{ px: 5 }}>
                      <SkeletonText
                        variant="large"
                        numberOfLine={8}
                        lastWidth="100%"
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              }
            />
          </Table>
        </TableContainer>
      </Box>
    </CellFrame>
  )
}

function isUserItemSerial(
  serial: UserItemSerial | SerialItemOwnersFragment['edges'][number]
): serial is UserItemSerial {
  return 'serialId' in serial
}
